/**
 * CTRL_06_ConfigPanel - Model Performance Monitoring Configuration
 */
import React, { useState, useEffect } from 'react';
import { Card, Switch, Button, Collapse, Select, Tag, Alert, Spin } from 'antd';
import {
  SettingOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  SaveOutlined
} from '@ant-design/icons';
import { getControlConfig, saveControlConfig } from '../api/trustgridApi';
import { showError, showSuccess } from '../../../utils/notifications';

const { Panel } = Collapse;
const { Option } = Select;

// Performance metrics
const PERFORMANCE_METRICS = [
  { value: 'accuracy', label: 'Accuracy', description: 'Model prediction accuracy' },
  { value: 'precision', label: 'Precision', description: 'True positive / (True positive + False positive)' },
  { value: 'recall', label: 'Recall', description: 'True positive / (True positive + False negative)' },
  { value: 'f1_score', label: 'F1 Score', description: 'Harmonic mean of precision and recall' },
  { value: 'latency_ms', label: 'Latency (ms)', description: 'Response time in milliseconds' },
  { value: 'throughput', label: 'Throughput', description: 'Requests per second' },
  { value: 'error_rate', label: 'Error Rate', description: 'Percentage of failed requests' }
];

const MONITORING_INTERVALS = [
  { value: 'realtime', label: 'Real-time', description: 'Continuous monitoring' },
  { value: 'hourly', label: 'Hourly', description: 'Check every hour' },
  { value: 'daily', label: 'Daily', description: 'Check once per day' },
  { value: 'weekly', label: 'Weekly', description: 'Check once per week' }
];

function CTRL_06_ConfigPanel({ projectId, controlCode, project }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeKeys, setActiveKeys] = useState(['1']);

  useEffect(() => {
    if (projectId && controlCode) {
      fetchConfig();
    }
  }, [projectId, controlCode]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await getControlConfig(projectId, controlCode);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
      setConfig({
        enabled: false,
        config: {
          required_metrics: ['accuracy', 'latency_ms'],
          degradation_threshold_pct: 5,
          monitoring_interval: 'daily',
          enable_drift_detection: true
        }
      });
      if (error.response && error.response.status !== 404) {
        showError('Failed to load configuration. Using defaults.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveControlConfig(projectId, controlCode, config);
      showSuccess('Configuration saved successfully');
      setHasChanges(false);
      fetchConfig();
    } catch (error) {
      console.error('Failed to save config:', error);
      showError(error);
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (field, value) => {
    setConfig({
      ...config,
      config: {
        ...config.config,
        [field]: value
      }
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!config) return null;

  const configData = config.config || {};
  const isConfigured = config.enabled && configData.required_metrics?.length > 0;
  const statusIcon = isConfigured ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <WarningOutlined style={{ color: '#faad14' }} />;
  const statusText = isConfigured ? 'Configured' : 'Setup Required';

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <SettingOutlined />
          <span>CTRL_06 Configuration: Performance Monitoring</span>
          {statusIcon}
          <Tag color={isConfigured ? 'green' : 'orange'}>{statusText}</Tag>
        </div>
      }
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSave}
          loading={saving}
          disabled={!hasChanges}
        >
          Save Changes
        </Button>
      }
    >
      <Collapse activeKey={activeKeys} onChange={setActiveKeys} expandIconPosition="right">
        <Panel
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <strong>1. Enable Performance Monitoring</strong>
              {config.enabled && <Tag color="green">Active</Tag>}
            </div>
          }
          key="1"
        >
          <div style={{ padding: '16px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
              <Switch
                checked={config.enabled}
                onChange={(checked) => setConfig({ ...config, enabled: checked })}
                size="large"
              />
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  {config.enabled ? 'Performance Monitoring Enabled' : 'Performance Monitoring Disabled'}
                </div>
                <div style={{ color: '#888', fontSize: '13px' }}>
                  {config.enabled
                    ? 'Tracking model performance metrics and detecting drift'
                    : 'Enable to monitor model performance metrics over time'}
                </div>
              </div>
            </div>

            {!config.enabled && (
              <Alert
                type="info"
                message="About Performance Monitoring"
                description="This control tracks model performance metrics (accuracy, latency, throughput) and detects drift over time. Essential for maintaining model reliability in production."
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}
          </div>
        </Panel>

        <Panel
          header={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <strong>2. Monitoring Configuration</strong>
              {configData.required_metrics?.length > 0 && <Tag color="blue">{configData.required_metrics.length} metrics</Tag>}
            </div>
          }
          key="2"
          disabled={!config.enabled}
        >
          <div style={{ padding: '16px 0' }}>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                Required Metrics
                <span style={{ fontWeight: 'normal', color: '#888', marginLeft: '8px' }}>
                  ({configData.required_metrics?.length || 0} selected)
                </span>
              </label>
              <Select
                mode="multiple"
                value={configData.required_metrics || []}
                onChange={(value) => updateConfig('required_metrics', value)}
                style={{ width: '100%', marginBottom: '8px' }}
                placeholder="Select metrics to track"
              >
                {PERFORMANCE_METRICS.map(metric => (
                  <Option key={metric.value} value={metric.value}>
                    <div>
                      <strong>{metric.label}</strong> - {metric.description}
                    </div>
                  </Option>
                ))}
              </Select>
              <div style={{ fontSize: '12px', color: '#888' }}>
                Select performance metrics to track for all inference requests
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                Degradation Threshold (%)
              </label>
              <Select
                value={configData.degradation_threshold_pct || 5}
                onChange={(value) => updateConfig('degradation_threshold_pct', value)}
                style={{ width: '200px', marginBottom: '8px' }}
              >
                <Option value={1}>1% (Very Sensitive)</Option>
                <Option value={2}>2% (Sensitive)</Option>
                <Option value={5}>5% (Balanced)</Option>
                <Option value={10}>10% (Moderate)</Option>
                <Option value={15}>15% (Relaxed)</Option>
                <Option value={20}>20% (Very Relaxed)</Option>
              </Select>
              <div style={{ fontSize: '12px', color: '#888' }}>
                Alert when metrics degrade beyond this threshold
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                Monitoring Interval
              </label>
              <Select
                value={configData.monitoring_interval || 'daily'}
                onChange={(value) => updateConfig('monitoring_interval', value)}
                style={{ width: '200px', marginBottom: '8px' }}
              >
                {MONITORING_INTERVALS.map(interval => (
                  <Option key={interval.value} value={interval.value}>
                    {interval.label} - {interval.description}
                  </Option>
                ))}
              </Select>
              <div style={{ fontSize: '12px', color: '#888' }}>
                How frequently to check performance metrics
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Switch
                  checked={configData.enable_drift_detection ?? true}
                  onChange={(checked) => updateConfig('enable_drift_detection', checked)}
                />
                <label style={{ fontWeight: 'bold' }}>Enable Drift Detection</label>
              </div>
              <div style={{ fontSize: '12px', color: '#888', marginTop: '4px', marginLeft: '44px' }}>
                Detect statistical drift in model performance using z-score analysis
              </div>
            </div>

            <Alert
              type="success"
              message="Configuration Summary"
              description={
                <div>
                  <p><strong>Tracking {configData.required_metrics?.length || 0} metrics:</strong> {configData.required_metrics?.join(', ') || 'None'}</p>
                  <p><strong>Degradation Threshold:</strong> {configData.degradation_threshold_pct || 5}%</p>
                  <p><strong>Monitoring Interval:</strong> {configData.monitoring_interval || 'daily'}</p>
                  <p><strong>Drift Detection:</strong> {configData.enable_drift_detection ? 'Enabled' : 'Disabled'}</p>
                </div>
              }
              showIcon
              style={{ marginTop: '16px' }}
            />
          </div>
        </Panel>
      </Collapse>
    </Card>
  );
}

export default CTRL_06_ConfigPanel;
